import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HfsOperationStateReflectionPayload } from '~/domain/assets/models/HfsOperationStateReflectionPayload';
import type { AppDispatch } from '~/store/Store';
import type { AppState } from '~/reducers';
import { patchRoboticMowerHfsSettingValue } from '~/api/robotic-mower-api/robotic-mower-api';
import { clearOperationById } from '~/domain/assets/store/hfs-operations/reducer';
import { mapSettingDataUpdates } from '~/domain/roboticmower-details/components/RoboticMowerSettingsCard/utils/utils';
import type {
  SettingControl,
  SettingValuePair,
  Update,
  UpdateError,
} from '../../components/RoboticMowerSettingsCard/types';
import { Status } from '../../components/RoboticMowerSettingsCard/types';

export const OUTDATED_THRESHOLD = 120000;

export const initialState: Fields = {
  assetId: '',
  initialSettings: [],
  drafts: [],
  updates: [],
  errors: [],
  categoriesExpandedStates: {},
};

export interface Fields {
  // Current asset ID and settings copy
  assetId: string;
  initialSettings: SettingControl[];
  // Non-approved draft values changes
  drafts: SettingValuePair[];
  // Formatted updates
  updates: Update[];
  // Errors
  errors: UpdateError[];
  categoriesExpandedStates: {
    [key: string]: boolean;
  };
}

const reducer = createSlice({
  name: 'robotic-settings',
  initialState,
  reducers: {
    initialize: (
      state,
      action: PayloadAction<{ initialSettings: SettingControl[]; assetId: string }>
    ) => {
      const { initialSettings, assetId } = action.payload;
      state.initialSettings = initialSettings;
      state.assetId = assetId;
    },
    setCategoryExpandedState: (
      state,
      action: PayloadAction<{ categoryId: string; expanded: boolean }>
    ) => {
      state.categoriesExpandedStates[action.payload.categoryId] = action.payload.expanded;
    },
    markOutdatedAsFailed: (
      state,
      action: PayloadAction<{ currentTimestamp: number; assetId: string }>
    ) => {
      const { currentTimestamp, assetId } = action.payload;
      state.updates = state.updates.map((update) =>
        update.assetId === assetId &&
        update.status === Status.RUNNING &&
        update.timestamp !== undefined &&
        update.timestamp <= currentTimestamp - OUTDATED_THRESHOLD
          ? { ...update, status: Status.FAILED }
          : update
      );
    },
    resetDrafts: (state) => {
      state.drafts = [];
    },
    addDraft: (state, action: PayloadAction<SettingValuePair>) => {
      const draft = action.payload;
      const cleanedFromPrev = state.drafts.filter((item) => item.settingType != draft.settingType);
      cleanedFromPrev.push(draft);
      state.drafts = cleanedFromPrev;
    },
    setError: (state, action: PayloadAction<UpdateError>) => {
      state.errors = state.errors.filter(
        (error: UpdateError) =>
          error.assetId != action.payload.assetId ||
          action.payload.settingType !== error.settingType
      );
      state.errors.push(action.payload);
    },
    clearDraft: (state, action: PayloadAction<string>) => {
      const settingTypeToRemove = action.payload;
      state.drafts = state.drafts.filter((item) => settingTypeToRemove !== item.settingType);
    },
    clearUpdates: (state, action: PayloadAction<string[]>) => {
      state.updates = state.updates.filter(
        (update) => update.assetId != state.assetId || !action.payload.includes(update.settingType)
      );
    },
    clearErrors: (state, action: PayloadAction<string[]>) => {
      state.errors = state.errors.filter(
        (error: UpdateError) =>
          error.assetId != state.assetId || !action.payload.includes(error.settingType)
      );
    },
    queueUpdates: (
      state,
      action: PayloadAction<
        Omit<
          Update,
          'assetId' | 'status' | 'operationIds' | 'successOperationIds' | 'failedOperationIds'
        >[]
      >
    ) => {
      const assetId = state.assetId;
      const listCopy = state.updates;
      action.payload.forEach((item) => {
        listCopy.push({
          ...item,
          assetId,
          status: Status.QUEUED,
          timestamp: Date.now(),
          operationIds: [],
          successOperationIds: [],
          failedOperationIds: [],
        });
      });
      state.updates = listCopy;
    },
    setUpdatesStatus: (
      state,
      action: PayloadAction<{ settingTypes: string[]; status: Status; assetId: string }>
    ) => {
      const assetId = action.payload.assetId;
      state.updates = state.updates.map((item) => {
        if (item.assetId === assetId && action.payload.settingTypes.includes(item.settingType)) {
          return {
            ...item,
            status: action.payload.status,
          };
        }
        return item;
      });
    },
    assignUpdateOperationIds: (
      state,
      action: PayloadAction<{ settingType: string; operationIds: string[]; assetId: string }>
    ) => {
      const assetId = action.payload.assetId;
      state.updates = state.updates.map((item) => {
        if (item.assetId === assetId && action.payload.settingType === item.settingType) {
          return {
            ...item,
            operationIds: action.payload.operationIds,
          };
        }
        return item;
      });
    },
    handleOperationSuccess: (state, action: PayloadAction<string>) => {
      const operationId = action.payload;
      state.updates = state.updates.map((item) => {
        if (
          item.operationIds.includes(operationId) &&
          !item.successOperationIds.includes(operationId)
        ) {
          return {
            ...item,
            successOperationIds: [...item.successOperationIds, operationId],
          };
        }
        return item;
      });
    },
    clearUpdatesByOperationId: (state, action: PayloadAction<string>) => {
      state.updates = state.updates.filter((item) => !item.operationIds?.includes(action.payload));
    },
    handleOperationFailure: (state, action: PayloadAction<string>) => {
      const operationId = action.payload;
      state.updates = state.updates.map((item) => {
        if (
          item.operationIds.includes(operationId) &&
          !item.failedOperationIds.includes(operationId)
        ) {
          return {
            ...item,
            failedOperationIds: [...item.failedOperationIds, operationId],
            status: Status.FAILED,
          };
        }
        return item;
      });
    },
    resetQueuedUpdates: (state) => {
      const assetId = state.assetId;
      state.updates = state.updates.filter(
        (item) => item.assetId !== assetId || item.status != Status.QUEUED
      );
    },
  },
});

export const {
  initialize,
  resetQueuedUpdates,
  addDraft,
  setError,
  clearDraft,
  clearErrors,
  clearUpdates,
  queueUpdates,
  resetDrafts,
  setUpdatesStatus,
  setCategoryExpandedState,
  assignUpdateOperationIds,
  markOutdatedAsFailed,
  handleOperationSuccess,
  handleOperationFailure,
  clearUpdatesByOperationId,
} = reducer.actions;

export default reducer.reducer;

export const reflectOperationStatusUpdate =
  (payload: HfsOperationStateReflectionPayload) =>
  (dispatch: AppDispatch, getState: () => AppState) => {
    const { operationId, status } = payload;
    const settingUpdate = getState().roboticSettingsStates.updates.find((update) =>
      update.operationIds.includes(operationId)
    );

    if (settingUpdate !== undefined) {
      if (status === 'Sent' || status === 'Completed') {
        dispatch(handleOperationSuccess(payload.operationId));
        const relevantUpdate = getState().roboticSettingsStates.updates.find((update) =>
          update.operationIds.includes(operationId)
        );
        if (
          relevantUpdate?.operationIds?.length === relevantUpdate?.successOperationIds?.length &&
          relevantUpdate !== undefined
        ) {
          const settingDataUpdates = mapSettingDataUpdates(relevantUpdate);
          if (settingDataUpdates.length > 0) {
            for (const settingDataUpdate of settingDataUpdates) {
              dispatch(
                patchRoboticMowerHfsSettingValue({
                  settingDataOverwrites: settingDataUpdate.settingDataUpdate,
                  hfsActionId: settingDataUpdate.hfsActionId,
                  assetId: relevantUpdate.assetId,
                })
              );
            }
          }
          dispatch(clearUpdatesByOperationId(operationId));
          dispatch(clearOperationById(payload.operationId));
        }
      }
      if (status === 'Error') {
        dispatch(handleOperationFailure(payload.operationId));
        dispatch(clearOperationById(payload.operationId));
      }
    }
  };
